import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { apiClient } from "@/api/apiClient";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }

        if (password.length < 8) {
            toast.error("Le mot de passe doit contenir au moins 8 caractères");
            return;
        }

        if (!token) {
            toast.error("Token de réinitialisation manquant. Veuillez recliquer sur le lien dans l'email.");
            return;
        }

        setIsLoading(true);

        try {
            await apiClient.auth.resetPassword({ token, password });
            setIsSuccess(true);
            toast.success("Mot de passe modifié avec succès !");
        } catch (error: any) {
            toast.error(error.response?.data?.error || "Le lien a expiré ou est invalide.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)]">
                <Card className="w-[350px]">
                    <CardHeader>
                        <CardTitle className="text-red-500">Lien invalide</CardTitle>
                        <CardDescription>
                            Le lien de réinitialisation est manquant ou invalide.
                        </CardDescription>
                    </CardHeader>
                    <CardFooter>
                        <Link to="/login">
                            <Button className="w-full">Retour à la connexion</Button>
                        </Link>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)]">
                <Card className="w-[350px]">
                    <CardHeader>
                        <div className="flex justify-center mb-4">
                            <CheckCircle2 className="h-12 w-12 text-green-500" />
                        </div>
                        <CardTitle className="text-center">Mot de passe modifié !</CardTitle>
                        <CardDescription className="text-center">
                            Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                        </CardDescription>
                    </CardHeader>
                    <CardFooter>
                        <Link to="/login" className="w-full">
                            <Button className="w-full">Se connecter</Button>
                        </Link>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Cast components to any to avoid TS errors
    const LabelAny = Label as any;
    const InputAny = Input as any;

    return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(222,25%,7%)]">
            <Card className="w-[350px]">
                <CardHeader>
                    <CardTitle>Nouveau mot de passe</CardTitle>
                    <CardDescription>
                        Choisissez un nouveau mot de passe sécurisé.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent>
                        <div className="grid w-full items-center gap-4">
                            <div className="flex flex-col space-y-1.5">
                                <LabelAny htmlFor="password">Nouveau mot de passe</LabelAny>
                                <div className="relative">
                                    <InputAny
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                        required
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col space-y-1.5">
                                <LabelAny htmlFor="confirmPassword">Confirmer le mot de passe</LabelAny>
                                <div className="relative">
                                    <InputAny
                                        id="confirmPassword"
                                        type={showPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Modifier le mot de passe
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
};

export default ResetPasswordPage;
